// Repeater renderer — plan §6.3. Tekrarlanabilir grup blok.
// İ8 (Finar UX P2): blok başlığı "Blok N" yerine "Kayıt N — <ilk metin alanı>"; şemada öğe
// adı alanı yok (itemLabel kit/şema senkronu gerektirir — TODO), genel ad kopya kaynağından.

import type { QuestionSnapshot } from "@mahir/shared";
import { FORM_FILL_COPY, repeaterFirstText } from "../../../lib/form-fill-copy";

interface SubField {
  id: string;
  label: string;
  type: "text" | "numeric" | "date";
}

interface Props {
  question: QuestionSnapshot;
  value: Record<string, unknown>[];
  onChange: (v: Record<string, unknown>[]) => void;
}

export function RepeaterRenderer({ question, value, onChange }: Props): JSX.Element {
  const fields = (question.options as { subFields?: SubField[] } | null)?.subFields ?? [];
  const blocks = value ?? [];
  const itemLabel = FORM_FILL_COPY.repeater.fallbackItem;
  const textFields = fields.map((f) => ({ key: f.id, type: f.type }));

  const addBlock = (): void => {
    const blank: Record<string, unknown> = {};
    for (const f of fields) blank[f.id] = f.type === "numeric" ? null : "";
    onChange([...blocks, blank]);
  };
  const removeBlock = (idx: number): void => {
    const next = blocks.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const updateField = (idx: number, fid: string, v: unknown): void => {
    const next = blocks.slice();
    next[idx] = { ...next[idx], [fid]: v };
    onChange(next);
  };

  if (fields.length === 0) {
    return (
      <div
        className="text-xs px-2 py-1 rounded"
        style={{ background: "var(--color-bg)", color: "var(--color-text-3)" }}
      >
        Bu repeater için alt alan tanımlanmamış.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {blocks.map((b, idx) => {
        const title = FORM_FILL_COPY.repeater.itemTitle(
          itemLabel,
          idx,
          repeaterFirstText(b, textFields),
        );
        return (
          <div
            key={idx}
            className="rounded-lg p-3"
            style={{
              background: "var(--color-bg)",
              border: "1px solid var(--color-border)",
            }}
          >
            <div className="flex items-center justify-between gap-3 mb-2">
              <span
                className="min-w-0 truncate"
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "var(--color-text-2)",
                }}
              >
                {title}
              </span>
              <button
                onClick={() => removeBlock(idx)}
                aria-label={`${title} — sil`}
                className="px-2 py-0.5 rounded text-xs"
                style={{
                  background: "transparent",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-error)",
                }}
              >
                Sil
              </button>
            </div>
            <div className="flex flex-col gap-2">
              {fields.map((f) => (
                <div key={f.id} className="flex items-center gap-3">
                  <label
                    style={{
                      minWidth: 140,
                      color: "var(--color-text-2)",
                      fontSize: "0.82rem",
                    }}
                  >
                    {f.label}
                  </label>
                  <SubInput field={f} value={b[f.id]} onChange={(v) => updateField(idx, f.id, v)} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <button
        onClick={addBlock}
        className="px-3 py-1.5 rounded-lg text-xs self-start"
        style={{
          background: "transparent",
          border: "1px solid var(--color-border)",
          color: "var(--color-primary)",
        }}
      >
        {FORM_FILL_COPY.repeater.add(itemLabel)}
      </button>
    </div>
  );
}

function SubInput({
  field,
  value,
  onChange,
}: {
  field: SubField;
  value: unknown;
  onChange: (v: unknown) => void;
}): JSX.Element {
  const base = {
    className: "flex-1 rounded px-2 py-1 text-sm",
    style: {
      background: "var(--color-surface)",
      border: "1px solid var(--color-border)",
      color: "var(--color-text)",
    } as const,
  };
  if (field.type === "text") {
    return (
      <input
        type="text"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        {...base}
      />
    );
  }
  if (field.type === "numeric") {
    return (
      <input
        type="number"
        value={typeof value === "number" ? value : ""}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? null : Number(raw));
        }}
        {...base}
      />
    );
  }
  return (
    <input
      type="date"
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value || null)}
      {...base}
    />
  );
}
